import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
    Alert,
    CircularProgress,
    useTheme,
    useMediaQuery,
    Paper,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningIcon from '@mui/icons-material/Warning';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import DefaultLayout from '../layouts/DefaultLayout';
import GlobalHeader from '../components/GlobalHeader';
import TabPanel from '../components/TabPanel';
import { queryTripDetail, Trip, queryItemTypes, queryItemTypesWithLocations, queryPackingOperations, type ItemType } from '../schema';
import { useState, useMemo, useCallback, useEffect } from 'react';
import RouteMapVisualization from '../components/RouteMapVisualization';
import TripTimeline from '../components/TripTimeline';
import RouteVisualization from '../components/RouteVisualization';
import CreateOperationDialog from '../components/CreateOperationDialog';
import EditTripDialog from '../components/EditTripDialog';
import { inferRouteStructure } from '../utils/routeInference';
import { suggestPackingOperations } from '../utils/suggestPackingOperations';
import { quantityPlansFromLpSolution } from '../utils/quantityCalculation';
import { computeInsufficientItemsFromPlans } from '../utils/inventoryCheckLogic';
import { rebuildDagAndLpForTrip, type RebuildProgressStep } from '../utils/tripRebuildTrigger';
import { type DagNodeRow, type DagEdgeRow, getLpSolutionFromTrip, updateTripLpSolutionEdgeFlow } from '../utils/tripLpDb';
import { isPlanStale } from '../utils/planInputsFingerprint';
import {
    buildIncompatibilityKeySet,
    buildTrailersPerEdge,
    collectTrailerCargoInfeasibilityIssues,
    incompatibilityPairsFromRows,
    validateTripEdgeFlowChange,
    type InfeasibilityIssue,
} from '../utils/trailerCargoBan';
import QuantitySummary from '../components/QuantitySummary';
import SupplyFlowSection from '../components/SupplyFlowSection';
import type { SuggestedOperation } from '../utils/suggestPackingOperations';
import OperationsListSection from '../components/OperationsListSection';
import { formatOperationSummary, getOperationProgress, splitOperationsActiveCompleted } from '../utils/operationDisplay';
import type { OperationSummaryInput } from '../utils/operationDisplay';
import { locationKey, type LpDagNodeRow, type LpEdgeFlowRow } from '../utils/packingTaskGenerator';
import { filterValidItemLocations } from '../utils/instantDbGuards';
import { TRIP_PLAN_LOCK_MESSAGE, useTripLock } from '../hooks/useTripLock';
import { db } from '../instant';
import { id } from '@instantdb/react';
import { format } from 'date-fns';
import { getInventoryItemUrl } from '../constants';

/** Parse event date from DB; returns null for missing or invalid values so the screen still loads. */
function parseEventDate(value: string | number | null | undefined): Date | null {
    if (value == null || value === '') return null;
    const d = new Date(value as string | number);
    return Number.isNaN(d.getTime()) ? null : d;
}

const TRIP_TAB_OVERVIEW = 0;
const TRIP_TAB_SUPPLY_FLOW = 1;
const TRIP_TAB_OPERATIONS = 2;
const TRIP_TAB_INVENTORY = 3;
const TRIP_TAB_ISSUES = 4;

const EMPTY_TRAILER_CARGO_INC: Array<{
    id?: string;
    trailerItemType?: { id: string } | null;
    cargoItemType?: { id: string } | null;
}> = [];

const RECALC_STEPS: { step: RebuildProgressStep; label: string }[] = [
    { step: 'loading', label: 'Loading trip data' },
    { step: 'route', label: 'Building route structure' },
    { step: 'dag', label: 'Writing trip DAG' },
    { step: 'requirements', label: 'Building requirements' },
    { step: 'solve', label: 'Solving supply flow' },
    { step: 'lpClear', label: 'Clearing old solution' },
    { step: 'lpWrite', label: 'Writing new solution' },
];

function RecalculateStepsList({ currentStep }: { currentStep: RebuildProgressStep | null }) {
    const stepIndex =
        currentStep !== null
            ? RECALC_STEPS.findIndex(s => s.step === currentStep)
            : 0; /* null = just started, show first step as current */
    return (
        <List dense disablePadding sx={{ py: 0 }}>
            {RECALC_STEPS.map(({ step, label }, i) => {
                const isCompleted = stepIndex >= 0 && i < stepIndex;
                const isCurrent = stepIndex >= 0 && i === stepIndex;
                return (
                    <ListItem key={step} disablePadding sx={{ alignItems: 'flex-start', gap: 1.5, py: 0.75 }}>
                        {isCompleted && <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20, mt: 0.25, flexShrink: 0 }} />}
                        {isCurrent && <CircularProgress size={20} sx={{ mt: 0.25, flexShrink: 0 }} />}
                        {!isCompleted && !isCurrent && <Box sx={{ width: 20, height: 20, flexShrink: 0 }} />}
                        <ListItemText
                            primary={label}
                            primaryTypographyProps={{
                                variant: 'body2',
                                color: isCompleted ? 'text.primary' : isCurrent ? 'text.primary' : 'text.secondary',
                                fontWeight: isCurrent ? 600 : 400,
                            }}
                        />
                    </ListItem>
                );
            })}
        </List>
    );
}

const TRIP_TAB_IDS = ['overview', 'supply-flow', 'operations', 'inventory', 'issues'] as const;

const recalcIssuePulseSx = {
    '@keyframes recalcIssuePulse': {
        '0%': { boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.55)' },
        '60%': { boxShadow: '0 0 0 10px rgba(211, 47, 47, 0)' },
        '100%': { boxShadow: '0 0 0 0 rgba(211, 47, 47, 0)' },
    },
    /** Long enough to notice after an LP failure; total duration = 1.35s × iterations */
    animation: 'recalcIssuePulse 1.35s ease-out 3',
} as const;

function TrailerCargoRoutingIssueAlert({
    issue,
    getItemTypeName,
    getTrailerName,
    highlight,
}: {
    issue: InfeasibilityIssue;
    getItemTypeName: (id: string) => string;
    getTrailerName: (id: string) => string;
    highlight?: boolean;
}) {
    const cargoName = getItemTypeName(issue.cargoItemTypeId);
    const alertSx = highlight ? recalcIssuePulseSx : undefined;
    if (issue.transport) {
        const { fromLabel, destLabel, trailerIds } = issue.transport;
        return (
            <Alert severity="error" sx={alertSx}>
                <Typography variant="body2" component="div">
                    Cannot transport <Link to={getInventoryItemUrl(issue.cargoItemTypeId)}>{cargoName}</Link> from {fromLabel} to{' '}
                    {destLabel}
                    {trailerIds.length > 0 ? (
                        <>
                            {' '}
                            via trailer(s):{' '}
                            {trailerIds.map((tid, i) => (
                                <span key={tid}>
                                    {i > 0 ? ', ' : ''}
                                    <Link to={getInventoryItemUrl(tid)}>{getTrailerName(tid)}</Link>
                                </span>
                            ))}
                        </>
                    ) : (
                        <> via trailer(s): (no trailers assigned on this leg)</>
                    )}
                </Typography>
            </Alert>
        );
    }
    return (
        <Alert severity="error" sx={alertSx}>
            <Typography variant="body2" component="div">
                {issue.message}
            </Typography>
            <Typography variant="body2" component="div" sx={{ mt: 0.75 }}>
                <Link to={getInventoryItemUrl(issue.cargoItemTypeId)}>{cargoName}</Link>
            </Typography>
        </Alert>
    );
}

export default function TripDetailScreen() {
    const { tripId, tab: tabParam } = useParams<{ tripId: string; tab?: string }>();
    const navigate = useNavigate();
    const { lockedTripIds } = useTripLock();
    const tripLocked = tripId ? lockedTripIds.has(tripId) : false;
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [eventsEditMode, setEventsEditMode] = useState(false);
    const [supplyFlowEditMode, setSupplyFlowEditMode] = useState(false);
    const [selectedSuggestion, setSelectedSuggestion] = useState<SuggestedOperation | null>(null);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [lpUpdating, setLpUpdating] = useState(false);
    const [lpProgressStep, setLpProgressStep] = useState<RebuildProgressStep | null>(null);
    /** After a failed recalculate, pulse-highlight matching row(s) in Trailer–cargo routing; auto-clears. */
    const [recalcHighlightCargoItemTypeId, setRecalcHighlightCargoItemTypeId] = useState<string | null>(null);
    const [lpSuccess, setLpSuccess] = useState(false);
    const [supplyFlowBanError, setSupplyFlowBanError] = useState<string | null>(null);
    /** Draft dates while editing; persisted to DB on blur to avoid re-renders on every keystroke */
    const [draftEventDates, setDraftEventDates] = useState<Record<string, { startDate?: Date | null; endDate?: Date | null }>>({});
    const [tripDuringRecalc, setTripDuringRecalc] = useState<Trip | null>(null);
    const tabIndex =
        tabParam && TRIP_TAB_IDS.includes(tabParam as (typeof TRIP_TAB_IDS)[number])
            ? TRIP_TAB_IDS.indexOf(tabParam as (typeof TRIP_TAB_IDS)[number])
            : 0;
    const activeTab = tabIndex;
    useEffect(() => {
        if (tripId && tabParam && !TRIP_TAB_IDS.includes(tabParam as (typeof TRIP_TAB_IDS)[number])) {
            navigate(`/logistics/trip/${tripId}/overview`, { replace: true });
        }
    }, [tripId, tabParam, navigate]);

    useEffect(() => {
        if (!recalcHighlightCargoItemTypeId) return;
        const t = setTimeout(() => setRecalcHighlightCargoItemTypeId(null), 9000);
        return () => clearTimeout(t);
    }, [recalcHighlightCargoItemTypeId]);
    const handleTripTabChange = useCallback(
        (_e: React.SyntheticEvent, value: number) => {
            if (tripId && value >= 0 && value < TRIP_TAB_IDS.length) {
                navigate(`/logistics/trip/${tripId}/${TRIP_TAB_IDS[value]}`);
            }
        },
        [tripId, navigate]
    );
    const [expandedSections, setExpandedSections] = useState<Set<string>>(
        new Set([
            'route',
            'events',
            'timeline',
            'supplyFlow',
            'suggested',
            'activeOperation',
            'completedOperations',
            'inventory',
            'issuesTrailerCargo',
            'issuesInventory',
            'issuesRoute',
        ])
    );

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleAccordionChange = useCallback(
        (id: string) => (_e: React.SyntheticEvent, isExpanded: boolean) => {
            setExpandedSections(prev => {
                const next = new Set(prev);
                if (isExpanded) next.add(id);
                else next.delete(id);
                return next;
            });
        },
        []
    );

    const { data, isLoading, error } = queryTripDetail(tripId ?? null);
    const trailerCargoIncompatibilityRows = useMemo(
        () => data?.trailerCargoIncompatibilities ?? EMPTY_TRAILER_CARGO_INC,
        [data?.trailerCargoIncompatibilities]
    );
    const { data: itemTypesData } = queryItemTypes();
    const { data: itemTypesWithLocationsData } = queryItemTypesWithLocations();
    const { data: operationsData } = queryPackingOperations();

    const trip = data?.trips?.[0] ?? null;

    /** When there is a global active operation linked to a different trip, show this instead of normal active section. */
    const activeOperationElsewhereMessage = useMemo(() => {
        if (!tripId || !operationsData?.packingOperations) return undefined;
        const globalActive = (
            operationsData.packingOperations as Array<{ status?: string; completedAt?: unknown; trip?: { id: string } | null }>
        ).find(op => op.status !== 'completed' && op.completedAt == null);
        if (!globalActive?.trip?.id) return undefined;
        if (globalActive.trip.id === tripId) return undefined;
        return 'An active packing operation exists but is linked to another trip. Complete or cancel it there, or create an operation from this trip.';
    }, [tripId, operationsData?.packingOperations]);

    const tripEvents = useMemo(() => {
        if (!trip?.events?.length) return [];
        const events = (
            trip.events as Array<{
                id: string;
                name?: string;
                startDate?: string | number | null;
                endDate?: string | number | null;
                packingListItems?: Array<{
                    itemType?: { id: string; category?: { name: string } | null } | null;
                    quantity?: number;
                }>;
            }>
        ).slice();
        events.sort((a, b) => {
            const aStart = parseEventDate(a.startDate)?.getTime() ?? 0;
            const bStart = parseEventDate(b.startDate)?.getTime() ?? 0;
            return aStart - bStart;
        });
        return events;
    }, [trip]);

    const eventsMap = useMemo(() => {
        const map = new Map<string, { id: string; name?: string; startDate?: Date | null; endDate?: Date | null }>();
        for (const e of tripEvents) {
            map.set(e.id, {
                id: e.id,
                name: e.name,
                startDate: parseEventDate(e.startDate),
                endDate: parseEventDate(e.endDate),
            });
        }
        return map;
    }, [tripEvents]);

    const packingListsForInference = useMemo(() => {
        const map = new Map<string, Array<{ itemType?: { id: string; category?: { name: string } | null } | null }>>();
        for (const e of tripEvents) {
            const items = (e.packingListItems ?? []).map(item => ({
                itemType: item.itemType
                    ? { id: item.itemType.id, category: item.itemType.category ? { name: item.itemType.category.name } : null }
                    : null,
            }));
            if (items.length > 0) map.set(e.id, items);
        }
        return map;
    }, [tripEvents]);

    const sourceStorageLocationId = (trip as { sourceStorageLocation?: { id: string } | null })?.sourceStorageLocation?.id ?? null;
    const destStorageLocationId = (trip as { destStorageLocation?: { id: string } | null })?.destStorageLocation?.id ?? null;

    const tripStorageLocationIds = useMemo(() => {
        const ids = [sourceStorageLocationId, destStorageLocationId].filter(Boolean) as string[];
        const set = new Set(ids);
        const out: string[] = [];
        for (const id of set) out.push(id);
        return out;
    }, [sourceStorageLocationId, destStorageLocationId]);

    const routeStructure = useMemo(() => {
        if (tripEvents.length === 0) return null;
        const targetIds = tripEvents.map(e => e.id);
        const sourceIds = sourceStorageLocationId ? [sourceStorageLocationId] : [];
        const destIds = destStorageLocationId ? [destStorageLocationId] : [];
        return inferRouteStructure([], targetIds, eventsMap, packingListsForInference, sourceIds, destIds);
    }, [tripEvents, eventsMap, packingListsForInference, sourceStorageLocationId, destStorageLocationId]);

    const getTrailerName = useCallback(
        (trailerId: string) =>
            itemTypesData?.itemTypes?.find((t: { id: string }) => t.id === trailerId)?.name ?? `Trailer ${trailerId.slice(0, 8)}`,
        [itemTypesData]
    );

    /** Global item locations (all events + storage) for availability vs global inventory */
    const allItemLocations = useMemo(() => {
        const locations: Array<{
            id: string;
            quantity: number;
            itemType?: { id: string } | null;
            event?: { id: string } | null;
            storageLocation?: { id: string } | null;
        }> = [];
        const itemTypes = itemTypesWithLocationsData?.itemTypes ?? [];
        for (const itemType of itemTypes) {
            const itemLocations = filterValidItemLocations(
                (
                    itemType as {
                        itemLocations?: Array<{ id: string; quantity?: number; event?: { id: string }; storageLocation?: { id: string } }>;
                    }
                ).itemLocations ?? []
            );
            for (const location of itemLocations) {
                locations.push({
                    id: location.id,
                    quantity: location.quantity ?? 0,
                    itemType: { id: itemType.id },
                    event: location.event ? { id: location.event.id } : null,
                    storageLocation: location.storageLocation ? { id: location.storageLocation.id } : null,
                });
            }
        }
        return locations;
    }, [itemTypesWithLocationsData]);

    /** Global available quantity for an item type (no exclusions) */
    const getAvailableGlobal = useCallback(
        (itemTypeId: string): number => {
            return allItemLocations.filter(loc => loc.itemType?.id === itemTypeId).reduce((sum, loc) => sum + loc.quantity, 0);
        },
        [allItemLocations]
    );

    const getItemTypeName = useCallback(
        (itemTypeId: string) => itemTypesData?.itemTypes?.find((t: { id: string }) => t.id === itemTypeId)?.name ?? itemTypeId,
        [itemTypesData]
    );

    const getItemTypeCategory = useCallback(
        (itemTypeId: string) =>
            (itemTypesData?.itemTypes?.find((t: { id: string }) => t.id === itemTypeId) as { category?: { name?: string } } | undefined)
                ?.category?.name ?? '',
        [itemTypesData]
    );

    const sourceName = (trip as { sourceStorageLocation?: { name?: string } | null })?.sourceStorageLocation?.name ?? 'HQ';
    const destName = (trip as { destStorageLocation?: { name?: string } | null })?.destStorageLocation?.name ?? 'Destination';

    /** DAG from DB for LP (sorted by index) */
    const dagNodesFromTrip = useMemo((): DagNodeRow[] => {
        const raw =
            (trip as { tripDagNodes?: Array<{ index: number; kind: string; eventId?: string | null; storageLocationId?: string | null }> })
                ?.tripDagNodes ?? [];
        return raw.slice().sort((a, b) => a.index - b.index) as DagNodeRow[];
    }, [trip]);
    const dagEdgesFromTrip = useMemo((): DagEdgeRow[] => {
        return ((trip as { tripDagEdges?: Array<{ fromNodeIndex: number; toNodeIndex: number }> })?.tripDagEdges ?? []) as DagEdgeRow[];
    }, [trip]);

    const trailersPerEdgeForTrip = useMemo(() => {
        if (!routeStructure || !sourceStorageLocationId || !destStorageLocationId || dagNodesFromTrip.length === 0) {
            return null;
        }
        return buildTrailersPerEdge(routeStructure, dagNodesFromTrip, sourceStorageLocationId, destStorageLocationId);
    }, [routeStructure, dagNodesFromTrip, sourceStorageLocationId, destStorageLocationId]);

    const incompatibilityKeysForTrip = useMemo(
        () => buildIncompatibilityKeySet(incompatibilityPairsFromRows(trailerCargoIncompatibilityRows)),
        [trailerCargoIncompatibilityRows]
    );

    /** DAG and LP from InstantDB query; supply flow updates when trip (tripDagNodes, lpSolution) update reactively. */
    const dagNodes = dagNodesFromTrip;
    const dagEdges = dagEdgesFromTrip;
    const effectiveLpSolution = getLpSolutionFromTrip(trip);

    /** Trip-wide required quantities from LP-DAG solution only (null when no LP solution or when route has date conflicts) */
    const quantityPlans = useMemo(() => {
        if (!routeStructure || routeStructure.trailerPaths.size === 0 || dagNodes.length === 0) return null;
        if (routeStructure.conflicts.length > 0) return null;
        const solution = effectiveLpSolution;
        if (!solution || Object.keys(solution).length === 0) return null;
        const nodeQuantitiesByItemType: Record<string, Array<{ nodeIndex: number; quantity: number }>> = {};
        const edgeFlowsByItemType: Record<string, Array<{ fromNodeIndex: number; toNodeIndex: number; value: number }>> = {};
        type SolEntry = {
            A: Array<{ nodeIndex: number; quantity: number }>;
            T: Array<{ fromNodeIndex: number; toNodeIndex: number; value: number }>;
        };
        for (const [itemTypeId, sol] of Object.entries(solution) as Array<[string, SolEntry]>) {
            nodeQuantitiesByItemType[itemTypeId] = sol.A;
            edgeFlowsByItemType[itemTypeId] = sol.T;
        }
        const getEventName = (id: string) => eventsMap.get(id)?.name ?? id;
        return quantityPlansFromLpSolution(
            dagNodes as Array<{ index: number; kind: 'source' | 'event' | 'dest'; eventId?: string | null }>,
            routeStructure,
            nodeQuantitiesByItemType,
            edgeFlowsByItemType,
            getEventName
        );
    }, [routeStructure, dagNodes, effectiveLpSolution, eventsMap]);

    /** Item types with insufficient global inventory for trip requirements */
    const insufficientGlobalItems = useMemo(() => {
        if (!quantityPlans || quantityPlans.size === 0) return [];
        const plansWithTotal = new Map(Array.from(quantityPlans.entries()).map(([id, p]) => [id, { totalNeeded: p.totalNeeded }]));
        return computeInsufficientItemsFromPlans(plansWithTotal, getAvailableGlobal);
    }, [quantityPlans, getAvailableGlobal]);

    const trailerItemTypes = useMemo(
        () => (itemTypesData?.itemTypes?.filter((t: ItemType) => t.category?.name === 'Trailers') ?? []) as ItemType[],
        [itemTypesData]
    );

    /** Packing lists by event for LP requirements (eventId -> [{ itemTypeId, quantity }]) */
    const packingListsByEvent = useMemo(() => {
        const map = new Map<string, Array<{ itemTypeId: string; quantity: number }>>();
        for (const e of tripEvents) {
            const items = (e.packingListItems ?? []).map(item => ({
                itemTypeId: item.itemType?.id ?? '',
                quantity: item.quantity ?? 0,
            }));
            if (items.length > 0) map.set(e.id, items);
        }
        return map;
    }, [tripEvents]);

    const handleRecalculatePlan = useCallback(async () => {
        if (!tripId || lpUpdating || !trip) return;
        setTripDuringRecalc(trip);
        setLpUpdating(true);
        setLpProgressStep(null);
        setRecalcHighlightCargoItemTypeId(null);
        setLpSuccess(false);
        try {
            const result = await rebuildDagAndLpForTrip(tripId, setLpProgressStep, trip, trailerCargoIncompatibilityRows);
            if (!result.ok) {
                setRecalcHighlightCargoItemTypeId(result.failure?.itemTypeId ?? null);
                navigate(`/logistics/trip/${tripId}/issues`);
            } else {
                setLpSuccess(true);
                setTimeout(() => setLpSuccess(false), 3000);
            }
        } catch (e) {
            const failure = e && typeof e === 'object' && 'failure' in e ? (e as { failure?: { itemTypeId?: string } }).failure : undefined;
            setRecalcHighlightCargoItemTypeId(failure?.itemTypeId ?? null);
            navigate(`/logistics/trip/${tripId}/issues`);
        } finally {
            setLpUpdating(false);
            setLpProgressStep(null);
            setTripDuringRecalc(null);
        }
    }, [tripId, lpUpdating, trip, trailerCargoIncompatibilityRows, navigate]);

    /** Persist a supply flow edit to DB (trips.lpSolution); no-op if trip has no solution yet. */
    const persistSupplyFlowEdit = useCallback(
        async (itemTypeId: string, fromNodeIndex: number, toNodeIndex: number, value: number) => {
            if (!tripId) return;
            const solution = getLpSolutionFromTrip(trip);
            if (!solution) return;
            if (trailersPerEdgeForTrip) {
                const ban = validateTripEdgeFlowChange(
                    itemTypeId,
                    fromNodeIndex,
                    toNodeIndex,
                    value,
                    trailersPerEdgeForTrip,
                    incompatibilityKeysForTrip
                );
                if (ban) {
                    setSupplyFlowBanError(ban);
                    return;
                }
            }
            setSupplyFlowBanError(null);
            await updateTripLpSolutionEdgeFlow(tripId, solution, itemTypeId, fromNodeIndex, toNodeIndex, value);
        },
        [tripId, trip, trailersPerEdgeForTrip, incompatibilityKeysForTrip]
    );

    /** LP data for CreateOperationDialog and Supply Flow; recomputes when trip (tripDagNodes, lpSolution) update from query */
    const tripLpData = useMemo((): { dagNodes: LpDagNodeRow[]; edgeFlowsByItemType: Record<string, LpEdgeFlowRow[]> } | undefined => {
        if (dagNodes.length === 0) return undefined;
        const solution = effectiveLpSolution;
        if (!solution || Object.keys(solution).length === 0) return undefined;
        const edgeFlowsByItemType: Record<string, LpEdgeFlowRow[]> = {};
        for (const [itemTypeId, sol] of Object.entries(solution) as Array<[string, { T: LpEdgeFlowRow[] }]>) {
            edgeFlowsByItemType[itemTypeId] = sol.T;
        }
        return {
            dagNodes: dagNodes as LpDagNodeRow[],
            edgeFlowsByItemType,
        };
    }, [dagNodes, effectiveLpSolution]);

    const routeNodes = useMemo(() => {
        if (!trip?.events?.length) return [];
        const events = (trip.events as Array<{ id: string; name?: string; startDate?: string | null; endDate?: string | null }>).slice();
        events.sort((a, b) => {
            const aStart = parseEventDate(a.startDate)?.getTime() ?? 0;
            const bStart = parseEventDate(b.startDate)?.getTime() ?? 0;
            return aStart - bStart;
        });
        const startNode = {
            id: sourceStorageLocationId ? locationKey('storage', sourceStorageLocationId) : 'hq',
            label: sourceName,
            startDate: null as Date | null,
            endDate: null as Date | null,
        };
        const endNode = {
            id: destStorageLocationId ? locationKey('storage', destStorageLocationId) : 'destination',
            label: destName,
            startDate: null as Date | null,
            endDate: null as Date | null,
        };
        return [
            startNode,
            ...events.map(e => ({
                id: e.id,
                label: e.name ?? e.id,
                startDate: parseEventDate(e.startDate),
                endDate: parseEventDate(e.endDate),
            })),
            endNode,
        ];
    }, [trip, sourceName, destName, sourceStorageLocationId, destStorageLocationId]);

    const timelineEvents = useMemo(() => {
        if (tripEvents.length === 0) return [];
        return tripEvents.map(e => ({
            id: e.id as string | null,
            label: e.name ?? e.id,
            startDate: parseEventDate(e.startDate) ?? new Date(0),
            endDate: parseEventDate(e.endDate) ?? new Date(0),
        }));
    }, [tripEvents]);

    const getNodeLabel = useCallback(
        (nodeId: string) => {
            if (nodeId.startsWith('storage:source:')) {
                const storageId = nodeId.slice(15);
                return storageId === sourceStorageLocationId
                    ? sourceName
                    : ((trip as { sourceStorageLocation?: { name?: string } })?.sourceStorageLocation?.name ?? 'Storage');
            }
            if (nodeId.startsWith('storage:dest:')) {
                const storageId = nodeId.slice(14);
                return storageId === destStorageLocationId
                    ? destName
                    : ((trip as { destStorageLocation?: { name?: string } })?.destStorageLocation?.name ?? 'Storage');
            }
            if (nodeId.startsWith('storage:')) {
                const storageId = nodeId.slice(8);
                if (storageId === sourceStorageLocationId) return sourceName;
                if (storageId === destStorageLocationId) return destName;
                return 'Storage';
            }
            if (nodeId === 'hq' || nodeId === 'destination') return nodeId === 'hq' ? sourceName : destName;
            const events = (trip?.events ?? []) as Array<{ id: string; name?: string }>;
            return events.find(e => e.id === nodeId)?.name ?? nodeId;
        },
        [trip, sourceName, destName, sourceStorageLocationId, destStorageLocationId]
    );

    /** Resolve DAG node index to display label (for supply flow section) */
    const getNodeLabelByIndex = useCallback(
        (nodeIndex: number) => {
            const node = dagNodes[nodeIndex] as DagNodeRow & { storageLocationId?: string | null };
            if (!node) return '';
            const nodeId =
                node.kind === 'source'
                    ? `storage:source:${node.storageLocationId ?? ''}`
                    : node.kind === 'dest'
                      ? `storage:dest:${node.storageLocationId ?? ''}`
                      : (node.eventId ?? '');
            return getNodeLabel(nodeId);
        },
        [dagNodes, getNodeLabel]
    );

    const trailerCargoIssues = useMemo(() => {
        if (!trailersPerEdgeForTrip || dagNodes.length === 0 || dagEdges.length === 0) return [];
        return collectTrailerCargoInfeasibilityIssues({
            dagNodes,
            edges: dagEdges,
            trailersPerEdge: trailersPerEdgeForTrip,
            incompatibilityKeys: incompatibilityKeysForTrip,
            packingListsByEvent,
            getCargoName: getItemTypeName,
            getTrailerName,
            getNodeLabel: getNodeLabelByIndex,
            getEventLabel: id => eventsMap.get(id)?.name ?? id,
        });
    }, [
        trailersPerEdgeForTrip,
        dagNodes,
        dagEdges,
        incompatibilityKeysForTrip,
        packingListsByEvent,
        getItemTypeName,
        getTrailerName,
        getNodeLabelByIndex,
        eventsMap,
    ]);

    const routeIssueEntries = useMemo(() => {
        if (!routeStructure) return [];
        const warnings = routeStructure.warnings.filter(w => w.severity !== 'info');
        return [
            ...routeStructure.conflicts.map((c, i) => ({
                key: `conflict-${i}`,
                severity: 'error' as const,
                text: `Date conflict (${getTrailerName(c.trailerId)}): ${c.reason}`,
            })),
            ...warnings.map((w, i) => ({
                key: `warn-${i}`,
                severity: w.severity === 'error' ? ('error' as const) : ('warning' as const),
                text: w.message,
            })),
        ];
    }, [routeStructure, getTrailerName]);

    const issuesTabCount = useMemo(
        () => trailerCargoIssues.length + insufficientGlobalItems.length + routeIssueEntries.length,
        [trailerCargoIssues, insufficientGlobalItems, routeIssueEntries]
    );

    /** Item types that appear in trip packing lists (for supply flow Autocomplete) */
    const planItemTypes = useMemo((): ItemType[] => {
        const itemTypeIds = new Set<string>();
        packingListsByEvent.forEach(items => {
            items.forEach(({ itemTypeId }) => itemTypeIds.add(itemTypeId));
        });
        const all = (itemTypesData?.itemTypes ?? []) as Array<ItemType & { category?: { name?: string } | null }>;
        return all.filter(t => itemTypeIds.has(t.id));
    }, [packingListsByEvent, itemTypesData?.itemTypes]);

    const canSuggestOperations =
        sourceStorageLocationId != null && destStorageLocationId != null && tripEvents.length > 0 && routeStructure != null;

    const suggestions = useMemo(() => {
        if (!canSuggestOperations || !routeStructure || sourceStorageLocationId == null || destStorageLocationId == null) return [];
        const eventNames = new Map<string, string>();
        for (const e of tripEvents) eventNames.set(e.id, e.name ?? e.id);
        return suggestPackingOperations(routeStructure, {
            sourceStorageLocationId,
            destStorageLocationId,
            eventIdsInOrder: tripEvents.map(e => e.id),
            eventNames,
            destName,
        });
    }, [canSuggestOperations, routeStructure, sourceStorageLocationId, destStorageLocationId, tripEvents, destName]);

    const displayTrip = lpUpdating ? (tripDuringRecalc ?? trip) : trip;

    /** Sorted events with parsed dates for Events table/cards (from displayTrip). */
    const eventsTableData = useMemo(() => {
        if (!displayTrip?.events?.length) return [];
        const events = (
            displayTrip.events as Array<{
                id: string;
                name?: string;
                startDate?: string | number | null;
                endDate?: string | number | null;
                packingListItems?: Array<{
                    id: string;
                    itemType?: { id: string; category?: { name: string } | null } | null;
                }>;
            }>
        ).slice();
        events.sort((a, b) => {
            const aStart = parseEventDate(a.startDate)?.getTime() ?? 0;
            const bStart = parseEventDate(b.startDate)?.getTime() ?? 0;
            return aStart - bStart;
        });
        return events.map(event => ({
            event,
            startDate: parseEventDate(event.startDate),
            endDate: parseEventDate(event.endDate),
            currentTrailers: trailerItemTypes.filter(tt => event.packingListItems?.some(pi => pi.itemType?.id === tt.id)),
        }));
    }, [displayTrip?.events, trailerItemTypes]);

    const planStale = useMemo(
        () => (trip ? isPlanStale(trip, trailerCargoIncompatibilityRows) : false),
        [trip, trailerCargoIncompatibilityRows]
    );

    const { active: activeOperations, completed: completedOperations } = useMemo(() => {
        const ops = (displayTrip?.packingOperations ?? []) as Trip['packingOperations'];
        return splitOperationsActiveCompleted(ops);
    }, [displayTrip?.packingOperations]);

    if (isLoading && !lpUpdating) {
        return (
            <DefaultLayout>
                <Box display="flex" justifyContent="center" padding={4}>
                    <CircularProgress />
                </Box>
            </DefaultLayout>
        );
    }

    if ((error || !trip) && !lpUpdating) {
        return (
            <DefaultLayout>
                <Alert severity="error">{error ? error.message : 'Trip not found'}</Alert>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/logistics/trips')}>
                    Back to Trips
                </Button>
            </DefaultLayout>
        );
    }

    if (!displayTrip) {
        return (
            <DefaultLayout>
                <Box display="flex" justifyContent="center" padding={4}>
                    <CircularProgress />
                </Box>
            </DefaultLayout>
        );
    }

    return (
        <DefaultLayout>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    width: '100%',
                    minWidth: 0,
                    height: '100%',
                    boxSizing: 'border-box',
                }}
            >
                <GlobalHeader title="" />
                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        py: 3,
                        px: { xs: 2, sm: 3 },
                        maxWidth: 900,
                        mx: 'auto',
                        width: '100%',
                        minWidth: 0,
                        boxSizing: 'border-box',
                    }}
                >
                    <Typography variant="h5" sx={{ mb: 1, textAlign: 'center' }}>
                        {displayTrip.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/logistics/trips')}>
                            Back to Trips
                        </Button>
                        <Tooltip title={tripLocked ? TRIP_PLAN_LOCK_MESSAGE : ''} disableHoverListener={!tripLocked}>
                            <span style={{ display: 'inline-block' }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    onClick={() => setEditDialogOpen(true)}
                                    disabled={tripLocked}
                                    sx={{ ml: 1 }}
                                >
                                    Edit trip
                                </Button>
                            </span>
                        </Tooltip>
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => setDeleteDialogOpen(true)}
                            sx={{ ml: 1 }}
                        >
                            Delete trip
                        </Button>
                        {sourceStorageLocationId && destStorageLocationId && !lpUpdating && (
                            <Tooltip title={tripLocked ? TRIP_PLAN_LOCK_MESSAGE : ''} disableHoverListener={!tripLocked}>
                                <span style={{ display: 'inline-block' }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<RefreshIcon />}
                                        onClick={handleRecalculatePlan}
                                        disabled={tripLocked}
                                        sx={{ ml: 1 }}
                                    >
                                        Recalculate plan
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                        {lpSuccess && (
                            <Typography variant="body2" color="success.main" sx={{ ml: 1 }}>
                                Plan recalculated
                            </Typography>
                        )}
                        {trip?.planRecalculatedAt != null && (
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                Last calculated: {format(new Date(trip.planRecalculatedAt), 'PPp')}
                            </Typography>
                        )}
                    </Box>
                    {tripLocked && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {TRIP_PLAN_LOCK_MESSAGE}
                        </Alert>
                    )}
                    {planStale && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Plan may be out of date — recalculate for updated flow and quantities.
                        </Alert>
                    )}
                    {displayTrip.description && (
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            {displayTrip.description}
                        </Typography>
                    )}

                    {/* Recalculate plan: detailed loading modal */}
                    <Dialog
                        open={lpUpdating}
                        disableEscapeKeyDown
                        PaperProps={{
                            sx: { minWidth: 320 },
                        }}
                    >
                        <DialogTitle>Recalculating plan</DialogTitle>
                        <DialogContent>
                            <RecalculateStepsList currentStep={lpProgressStep} />
                        </DialogContent>
                    </Dialog>

                    <Tabs
                        value={activeTab}
                        onChange={handleTripTabChange}
                        aria-label="trip detail tabs"
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{
                            minWidth: 0,
                            ...(activeTab === TRIP_TAB_ISSUES && issuesTabCount > 0
                                ? {
                                      '& .MuiTabs-indicator': {
                                          backgroundColor: 'error.main',
                                      },
                                  }
                                : {}),
                        }}
                    >
                        <Tab label="Overview" id="trip-detail-tab-0" aria-controls="trip-detail-tabpanel-0" />
                        <Tab label="Supply flow" id="trip-detail-tab-1" aria-controls="trip-detail-tabpanel-1" />
                        <Tab label="Operations" id="trip-detail-tab-2" aria-controls="trip-detail-tabpanel-2" />
                        <Tab label="Inventory" id="trip-detail-tab-3" aria-controls="trip-detail-tabpanel-3" />
                        <Tab
                            id="trip-detail-tab-4"
                            aria-controls="trip-detail-tabpanel-4"
                            label={
                                issuesTabCount > 0 ? (
                                    <Box
                                        component="span"
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            verticalAlign: 'middle',
                                        }}
                                    >
                                        <ErrorOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />
                                        <Box component="span" sx={{ color: 'error.main' }}>
                                            {`Issues (${issuesTabCount})`}
                                        </Box>
                                    </Box>
                                ) : (
                                    'Issues'
                                )
                            }
                        />
                    </Tabs>

                    <TabPanel value={activeTab} index={TRIP_TAB_OVERVIEW} idPrefix="trip-detail">
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflow: 'visible' }}>
                            <Accordion
                                expanded={expandedSections.has('route')}
                                onChange={handleAccordionChange('route')}
                                sx={{ boxShadow: 2, overflow: 'visible', '&:before': { display: 'none' } }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Route
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ overflow: 'visible', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <RouteMapVisualization
                                        nodes={routeNodes}
                                        routeStructure={routeStructure}
                                        getNodeLabel={getNodeLabel}
                                        getTrailerName={getTrailerName}
                                        onNodeClick={nodeId => {
                                            if (!nodeId.startsWith('storage:')) navigate(`/event/${nodeId}/map`);
                                        }}
                                    />
                                    {tripEvents.length > 0 && (
                                        <>
                                            <RouteVisualization
                                                routeStructure={routeStructure}
                                                getNodeLabel={getNodeLabel}
                                                getTrailerName={getTrailerName}
                                            />
                                            {routeStructure &&
                                                routeStructure.conflicts.length === 0 &&
                                                routeStructure.warnings.filter(w => w.severity === 'error').length === 0 &&
                                                (routeStructure.trailerPaths.size === 0 ? null : (
                                                    <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                                                        No conflicts detected
                                                    </Typography>
                                                ))}
                                            {routeStructure && routeStructure.conflicts.length > 0 && (
                                                <Box sx={{ mt: 1 }}>
                                                    {routeStructure.conflicts.map((c, i) => (
                                                        <Typography key={i} variant="body2" color="error.main">
                                                            Date conflict ({getTrailerName(c.trailerId)}): {c.reason}
                                                        </Typography>
                                                    ))}
                                                </Box>
                                            )}
                                            {routeStructure &&
                                                routeStructure.warnings.some(w => w.severity === 'error') &&
                                                routeStructure.warnings
                                                    .filter(w => w.severity === 'error')
                                                    .map((w, i) => (
                                                        <Typography key={i} variant="body2" color="error.main" sx={{ mt: 0.5 }}>
                                                            {w.message}
                                                        </Typography>
                                                    ))}
                                        </>
                                    )}
                                </AccordionDetails>
                            </Accordion>

                            <Accordion
                                expanded={expandedSections.has('events')}
                                onChange={handleAccordionChange('events')}
                                sx={{ boxShadow: 2, '&:before': { display: 'none' } }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            width: '100%',
                                            pr: 1,
                                        }}
                                    >
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Events
                                        </Typography>
                                        <IconButton
                                            component="div"
                                            role="button"
                                            tabIndex={0}
                                            aria-label={eventsEditMode ? 'View events' : 'Edit events'}
                                            onClick={e => {
                                                e.stopPropagation();
                                                setEventsEditMode(v => !v);
                                            }}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setEventsEditMode(v => !v);
                                                }
                                            }}
                                            size="small"
                                        >
                                            {eventsEditMode ? <VisibilityIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                                        </IconButton>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {(displayTrip.events?.length ?? 0) > 0 ? (
                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                            {isMobile ? (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                    {eventsTableData.map(({ event, startDate, endDate, currentTrailers }) => (
                                                        <Card key={event.id} variant="outlined">
                                                            <CardContent sx={{ '&:last-child': { pb: 1.5 } }}>
                                                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                                    Name
                                                                </Typography>
                                                                <Typography
                                                                    component="span"
                                                                    variant="body2"
                                                                    sx={{
                                                                        cursor: 'pointer',
                                                                        color: 'primary.main',
                                                                        textDecoration: 'underline',
                                                                        textUnderlineOffset: 2,
                                                                    }}
                                                                    onClick={() => navigate(`/event/${event.id}/map`)}
                                                                >
                                                                    {event.name ?? event.id}
                                                                </Typography>
                                                                <Typography
                                                                    variant="subtitle2"
                                                                    color="text.secondary"
                                                                    sx={{ mt: 1.5 }}
                                                                    gutterBottom
                                                                >
                                                                    Trailers
                                                                </Typography>
                                                                {eventsEditMode ? (
                                                                    <Autocomplete<ItemType, true>
                                                                        multiple
                                                                        size="small"
                                                                        options={trailerItemTypes}
                                                                        getOptionLabel={option => option.name ?? option.id}
                                                                        isOptionEqualToValue={(a, b) => a.id === b.id}
                                                                        value={currentTrailers}
                                                                        onChange={async (_e, newValue) => {
                                                                            const toAdd = newValue.filter(
                                                                                t => !currentTrailers.some(ct => ct.id === t.id)
                                                                            );
                                                                            const toRemove =
                                                                                event.packingListItems?.filter(
                                                                                    pi =>
                                                                                        pi.itemType?.category?.name === 'Trailers' &&
                                                                                        !newValue.some(n => n.id === pi.itemType?.id)
                                                                                ) ?? [];
                                                                            const txs: Parameters<typeof db.transact>[0] = [];
                                                                            for (const pi of toRemove) {
                                                                                txs.push(db.tx.packingListItems[pi.id].delete());
                                                                            }
                                                                            for (const t of toAdd) {
                                                                                const newId = id();
                                                                                txs.push(
                                                                                    db.tx.packingListItems[newId]
                                                                                        .update({
                                                                                            quantity: 1,
                                                                                            confirmed: false,
                                                                                        })
                                                                                        .link({ itemType: t.id })
                                                                                        .link({ event: event.id })
                                                                                );
                                                                            }
                                                                            if (txs.length > 0) await db.transact(txs);
                                                                        }}
                                                                        renderInput={params => <TextField {...params} variant="standard" />}
                                                                    />
                                                                ) : currentTrailers.length > 0 ? (
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                        {currentTrailers.map(t => (
                                                                            <Chip
                                                                                key={t.id}
                                                                                label={t.name ?? t.id}
                                                                                size="small"
                                                                                variant="outlined"
                                                                            />
                                                                        ))}
                                                                    </Box>
                                                                ) : (
                                                                    <Typography variant="body2">—</Typography>
                                                                )}
                                                                <Typography
                                                                    variant="subtitle2"
                                                                    color="text.secondary"
                                                                    sx={{ mt: 1.5 }}
                                                                    gutterBottom
                                                                >
                                                                    Start date
                                                                </Typography>
                                                                {eventsEditMode ? (
                                                                    <DatePicker
                                                                        value={
                                                                            draftEventDates[event.id]?.startDate !== undefined
                                                                                ? draftEventDates[event.id].startDate
                                                                                : startDate
                                                                        }
                                                                        onChange={date => {
                                                                            setDraftEventDates(prev => ({
                                                                                ...prev,
                                                                                [event.id]: {
                                                                                    ...prev[event.id],
                                                                                    startDate: date ?? null,
                                                                                },
                                                                            }));
                                                                        }}
                                                                        slotProps={{
                                                                            textField: {
                                                                                size: 'small',
                                                                                variant: 'standard',
                                                                                fullWidth: true,
                                                                                onBlur: () => {
                                                                                    setDraftEventDates(prev => {
                                                                                        const draft = prev[event.id]?.startDate;
                                                                                        if (draft !== undefined) {
                                                                                            const eventId = event.id;
                                                                                            const value =
                                                                                                draft == null
                                                                                                    ? null
                                                                                                    : Number.isFinite(draft.getTime())
                                                                                                      ? draft.getTime()
                                                                                                      : undefined;
                                                                                            if (value !== undefined) {
                                                                                                setTimeout(() => {
                                                                                                    db.transact([
                                                                                                        db.tx.events[eventId].update({
                                                                                                            startDate: value,
                                                                                                        }),
                                                                                                    ]);
                                                                                                }, 0);
                                                                                            }
                                                                                            const next = { ...prev };
                                                                                            if (next[event.id]) {
                                                                                                const { startDate: _, ...rest } =
                                                                                                    next[event.id];
                                                                                                if (Object.keys(rest).length === 0)
                                                                                                    delete next[event.id];
                                                                                                else
                                                                                                    next[event.id] = rest as {
                                                                                                        startDate?: Date | null;
                                                                                                        endDate?: Date | null;
                                                                                                    };
                                                                                            }
                                                                                            return next;
                                                                                        }
                                                                                        return prev;
                                                                                    });
                                                                                },
                                                                            },
                                                                            field: { clearable: true },
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <Typography variant="body2">
                                                                        {startDate ? format(startDate, 'M/d/yy') : '—'}
                                                                    </Typography>
                                                                )}
                                                                <Typography
                                                                    variant="subtitle2"
                                                                    color="text.secondary"
                                                                    sx={{ mt: 1.5 }}
                                                                    gutterBottom
                                                                >
                                                                    End date
                                                                </Typography>
                                                                {eventsEditMode ? (
                                                                    <DatePicker
                                                                        value={
                                                                            draftEventDates[event.id]?.endDate !== undefined
                                                                                ? draftEventDates[event.id].endDate
                                                                                : endDate
                                                                        }
                                                                        minDate={
                                                                            draftEventDates[event.id]?.startDate ?? startDate ?? undefined
                                                                        }
                                                                        onChange={date => {
                                                                            setDraftEventDates(prev => ({
                                                                                ...prev,
                                                                                [event.id]: {
                                                                                    ...prev[event.id],
                                                                                    endDate: date ?? null,
                                                                                },
                                                                            }));
                                                                        }}
                                                                        slotProps={{
                                                                            textField: {
                                                                                size: 'small',
                                                                                variant: 'standard',
                                                                                fullWidth: true,
                                                                                onBlur: () => {
                                                                                    setDraftEventDates(prev => {
                                                                                        const draft = prev[event.id]?.endDate;
                                                                                        if (draft !== undefined) {
                                                                                            const eventId = event.id;
                                                                                            const value =
                                                                                                draft == null
                                                                                                    ? null
                                                                                                    : Number.isFinite(draft.getTime())
                                                                                                      ? draft.getTime()
                                                                                                      : undefined;
                                                                                            if (value !== undefined) {
                                                                                                setTimeout(() => {
                                                                                                    db.transact([
                                                                                                        db.tx.events[eventId].update({
                                                                                                            endDate: value,
                                                                                                        }),
                                                                                                    ]);
                                                                                                }, 0);
                                                                                            }
                                                                                            const next = { ...prev };
                                                                                            if (next[event.id]) {
                                                                                                const { endDate: _, ...rest } =
                                                                                                    next[event.id];
                                                                                                if (Object.keys(rest).length === 0)
                                                                                                    delete next[event.id];
                                                                                                else
                                                                                                    next[event.id] = rest as {
                                                                                                        startDate?: Date | null;
                                                                                                        endDate?: Date | null;
                                                                                                    };
                                                                                            }
                                                                                            return next;
                                                                                        }
                                                                                        return prev;
                                                                                    });
                                                                                },
                                                                            },
                                                                            field: { clearable: true },
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <Typography variant="body2">
                                                                        {endDate ? format(endDate, 'M/d/yy') : '—'}
                                                                    </Typography>
                                                                )}
                                                            </CardContent>
                                                        </Card>
                                                    ))}
                                                </Box>
                                            ) : (
                                                <TableContainer>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                                                                <TableCell sx={{ fontWeight: 600 }}>Trailers</TableCell>
                                                                <TableCell sx={{ fontWeight: 600 }}>Start date</TableCell>
                                                                <TableCell sx={{ fontWeight: 600 }}>End date</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {eventsTableData.map(({ event, startDate, endDate, currentTrailers }) => (
                                                                <TableRow key={event.id} hover>
                                                                    <TableCell
                                                                        onClick={() => navigate(`/event/${event.id}/map`)}
                                                                        sx={{
                                                                            cursor: 'pointer',
                                                                            color: 'primary.main',
                                                                            textDecoration: 'underline',
                                                                            textUnderlineOffset: 2,
                                                                        }}
                                                                    >
                                                                        {event.name ?? event.id}
                                                                    </TableCell>
                                                                    <TableCell onClick={e => e.stopPropagation()}>
                                                                        {eventsEditMode ? (
                                                                            <Autocomplete<ItemType, true>
                                                                                multiple
                                                                                size="small"
                                                                                options={trailerItemTypes}
                                                                                getOptionLabel={option => option.name ?? option.id}
                                                                                isOptionEqualToValue={(a, b) => a.id === b.id}
                                                                                value={currentTrailers}
                                                                                onChange={async (_e, newValue) => {
                                                                                    const toAdd = newValue.filter(
                                                                                        t => !currentTrailers.some(ct => ct.id === t.id)
                                                                                    );
                                                                                    const toRemove =
                                                                                        event.packingListItems?.filter(
                                                                                            pi =>
                                                                                                pi.itemType?.category?.name ===
                                                                                                    'Trailers' &&
                                                                                                !newValue.some(
                                                                                                    n => n.id === pi.itemType?.id
                                                                                                )
                                                                                        ) ?? [];
                                                                                    const txs: Parameters<typeof db.transact>[0] = [];
                                                                                    for (const pi of toRemove) {
                                                                                        txs.push(db.tx.packingListItems[pi.id].delete());
                                                                                    }
                                                                                    for (const t of toAdd) {
                                                                                        const newId = id();
                                                                                        txs.push(
                                                                                            db.tx.packingListItems[newId]
                                                                                                .update({
                                                                                                    quantity: 1,
                                                                                                    confirmed: false,
                                                                                                })
                                                                                                .link({ itemType: t.id })
                                                                                                .link({ event: event.id })
                                                                                        );
                                                                                    }
                                                                                    if (txs.length > 0) await db.transact(txs);
                                                                                }}
                                                                                renderInput={params => (
                                                                                    <TextField {...params} variant="standard" />
                                                                                )}
                                                                            />
                                                                        ) : currentTrailers.length > 0 ? (
                                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                                {currentTrailers.map(t => (
                                                                                    <Chip
                                                                                        key={t.id}
                                                                                        label={t.name ?? t.id}
                                                                                        size="small"
                                                                                        variant="outlined"
                                                                                    />
                                                                                ))}
                                                                            </Box>
                                                                        ) : (
                                                                            <Typography variant="body2">—</Typography>
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell onClick={e => e.stopPropagation()}>
                                                                        {eventsEditMode ? (
                                                                            <DatePicker
                                                                                value={
                                                                                    draftEventDates[event.id]?.startDate !== undefined
                                                                                        ? draftEventDates[event.id].startDate
                                                                                        : startDate
                                                                                }
                                                                                onChange={date => {
                                                                                    setDraftEventDates(prev => ({
                                                                                        ...prev,
                                                                                        [event.id]: {
                                                                                            ...prev[event.id],
                                                                                            startDate: date ?? null,
                                                                                        },
                                                                                    }));
                                                                                }}
                                                                                slotProps={{
                                                                                    textField: {
                                                                                        size: 'small',
                                                                                        variant: 'standard',
                                                                                        fullWidth: true,
                                                                                        onBlur: () => {
                                                                                            setDraftEventDates(prev => {
                                                                                                const draft = prev[event.id]?.startDate;
                                                                                                if (draft !== undefined) {
                                                                                                    const eventId = event.id;
                                                                                                    const value =
                                                                                                        draft == null
                                                                                                            ? null
                                                                                                            : Number.isFinite(
                                                                                                                    draft.getTime()
                                                                                                                )
                                                                                                              ? draft.getTime()
                                                                                                              : undefined;
                                                                                                    if (value !== undefined) {
                                                                                                        setTimeout(() => {
                                                                                                            db.transact([
                                                                                                                db.tx.events[
                                                                                                                    eventId
                                                                                                                ].update({
                                                                                                                    startDate: value,
                                                                                                                }),
                                                                                                            ]);
                                                                                                        }, 0);
                                                                                                    }
                                                                                                    const next = { ...prev };
                                                                                                    if (next[event.id]) {
                                                                                                        const { startDate: _, ...rest } =
                                                                                                            next[event.id];
                                                                                                        if (Object.keys(rest).length === 0)
                                                                                                            delete next[event.id];
                                                                                                        else
                                                                                                            next[event.id] = rest as {
                                                                                                                startDate?: Date | null;
                                                                                                                endDate?: Date | null;
                                                                                                            };
                                                                                                    }
                                                                                                    return next;
                                                                                                }
                                                                                                return prev;
                                                                                            });
                                                                                        },
                                                                                    },
                                                                                    field: { clearable: true },
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <Typography variant="body2">
                                                                                {startDate ? format(startDate, 'M/d/yy') : '—'}
                                                                            </Typography>
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell onClick={e => e.stopPropagation()}>
                                                                        {eventsEditMode ? (
                                                                            <DatePicker
                                                                                value={
                                                                                    draftEventDates[event.id]?.endDate !== undefined
                                                                                        ? draftEventDates[event.id].endDate
                                                                                        : endDate
                                                                                }
                                                                                minDate={
                                                                                    draftEventDates[event.id]?.startDate ??
                                                                                    startDate ??
                                                                                    undefined
                                                                                }
                                                                                onChange={date => {
                                                                                    setDraftEventDates(prev => ({
                                                                                        ...prev,
                                                                                        [event.id]: {
                                                                                            ...prev[event.id],
                                                                                            endDate: date ?? null,
                                                                                        },
                                                                                    }));
                                                                                }}
                                                                                slotProps={{
                                                                                    textField: {
                                                                                        size: 'small',
                                                                                        variant: 'standard',
                                                                                        fullWidth: true,
                                                                                        onBlur: () => {
                                                                                            setDraftEventDates(prev => {
                                                                                                const draft = prev[event.id]?.endDate;
                                                                                                if (draft !== undefined) {
                                                                                                    const eventId = event.id;
                                                                                                    const value =
                                                                                                        draft == null
                                                                                                            ? null
                                                                                                            : Number.isFinite(
                                                                                                                    draft.getTime()
                                                                                                                )
                                                                                                              ? draft.getTime()
                                                                                                              : undefined;
                                                                                                    if (value !== undefined) {
                                                                                                        setTimeout(() => {
                                                                                                            db.transact([
                                                                                                                db.tx.events[
                                                                                                                    eventId
                                                                                                                ].update({
                                                                                                                    endDate: value,
                                                                                                                }),
                                                                                                            ]);
                                                                                                        }, 0);
                                                                                                    }
                                                                                                    const next = { ...prev };
                                                                                                    if (next[event.id]) {
                                                                                                        const { endDate: _, ...rest } =
                                                                                                            next[event.id];
                                                                                                        if (Object.keys(rest).length === 0)
                                                                                                            delete next[event.id];
                                                                                                        else
                                                                                                            next[event.id] = rest as {
                                                                                                                startDate?: Date | null;
                                                                                                                endDate?: Date | null;
                                                                                                            };
                                                                                                    }
                                                                                                    return next;
                                                                                                }
                                                                                                return prev;
                                                                                            });
                                                                                        },
                                                                                    },
                                                                                    field: { clearable: true },
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <Typography variant="body2">
                                                                                {endDate ? format(endDate, 'M/d/yy') : '—'}
                                                                            </Typography>
                                                                        )}
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            )}
                                        </LocalizationProvider>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            No events in this trip
                                        </Typography>
                                    )}
                                </AccordionDetails>
                            </Accordion>

                            <Accordion
                                expanded={expandedSections.has('timeline')}
                                onChange={handleAccordionChange('timeline')}
                                sx={{ boxShadow: 2, overflow: 'visible', '&:before': { display: 'none' } }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Timeline
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ overflow: 'visible' }}>
                                    <TripTimeline
                                        events={timelineEvents}
                                        width={600}
                                        routeStructure={routeStructure}
                                        getTrailerName={getTrailerName}
                                    />
                                </AccordionDetails>
                            </Accordion>
                        </Box>
                    </TabPanel>

                    <TabPanel value={activeTab} index={TRIP_TAB_SUPPLY_FLOW} idPrefix="trip-detail">
                        <Accordion
                            expanded={expandedSections.has('supplyFlow')}
                            onChange={handleAccordionChange('supplyFlow')}
                            sx={{ boxShadow: 2, overflow: 'visible', '&:before': { display: 'none' } }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Supply flow
                                    </Typography>
                                    <Tooltip title={tripLocked ? TRIP_PLAN_LOCK_MESSAGE : ''} disableHoverListener={!tripLocked}>
                                        <span>
                                            <IconButton
                                                component="div"
                                                role="button"
                                                tabIndex={0}
                                                aria-label={supplyFlowEditMode ? 'View supply flow' : 'Edit supply flow'}
                                                disabled={tripLocked}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    if (!tripLocked) setSupplyFlowEditMode(v => !v);
                                                }}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        if (!tripLocked) setSupplyFlowEditMode(v => !v);
                                                    }
                                                }}
                                                size="small"
                                            >
                                                {supplyFlowEditMode ? <VisibilityIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ overflow: 'visible' }}>
                                {supplyFlowBanError && (
                                    <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setSupplyFlowBanError(null)}>
                                        {supplyFlowBanError}
                                    </Alert>
                                )}
                                <SupplyFlowSection
                                    dagNodes={dagNodes}
                                    dagEdges={dagEdges}
                                    routeStructure={routeStructure}
                                    edgeFlowsByItemType={tripLpData?.edgeFlowsByItemType ?? {}}
                                    packingListsByEvent={packingListsByEvent}
                                    planItemTypes={planItemTypes}
                                    getItemTypeName={getItemTypeName}
                                    getNodeLabel={getNodeLabelByIndex}
                                    editMode={tripLocked ? false : supplyFlowEditMode}
                                    onEdgeFlowChange={persistSupplyFlowEdit}
                                    trailersPerEdge={trailersPerEdgeForTrip ?? undefined}
                                    incompatibilityKeys={incompatibilityKeysForTrip}
                                />
                            </AccordionDetails>
                        </Accordion>
                    </TabPanel>

                    <TabPanel value={activeTab} index={TRIP_TAB_OPERATIONS} idPrefix="trip-detail">
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Packing operations created from this tab follow the trip plan: task quantities come from the supply flow on
                                the previous tab rather than the packing lists and current item locations.
                            </Typography>
                            {canSuggestOperations && suggestions.length > 0 && (
                                <Accordion
                                    expanded={expandedSections.has('suggested')}
                                    onChange={handleAccordionChange('suggested')}
                                    sx={{ boxShadow: 2, '&:before': { display: 'none' } }}
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Suggested operations
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {isMobile ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                {suggestions.map((sug: SuggestedOperation, idx: number) => {
                                                    const sourceChips: Array<{ key: string; label: string }> = [];
                                                    if (sug.sourceStorageLocationIds.length > 0)
                                                        sourceChips.push({ key: 'src-storage', label: sourceName });
                                                    for (const eid of sug.sourceEventIds) {
                                                        const e = tripEvents.find(ev => ev.id === eid);
                                                        sourceChips.push({ key: eid, label: e?.name ?? eid });
                                                    }
                                                    const targetChips: Array<{ key: string; label: string }> = [];
                                                    if (sug.targetStorageLocationIds.length > 0)
                                                        targetChips.push({ key: 'tgt-storage', label: destName });
                                                    for (const eid of sug.targetEventIds) {
                                                        const e = tripEvents.find(ev => ev.id === eid);
                                                        targetChips.push({ key: eid, label: e?.name ?? eid });
                                                    }
                                                    return (
                                                        <Card key={idx} variant="outlined">
                                                            <CardContent sx={{ '&:last-child': { pb: 1.5 } }}>
                                                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                                    Description
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ mb: 1.5 }}>
                                                                    {sug.label}
                                                                </Typography>
                                                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                                    Sources
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                                                                    {sourceChips.length > 0
                                                                        ? sourceChips.map(({ key, label }) => (
                                                                              <Chip
                                                                                  key={key}
                                                                                  label={label}
                                                                                  size="small"
                                                                                  variant="outlined"
                                                                              />
                                                                          ))
                                                                        : '—'}
                                                                </Box>
                                                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                                    Targets
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                                                                    {targetChips.length > 0
                                                                        ? targetChips.map(({ key, label }) => (
                                                                              <Chip
                                                                                  key={key}
                                                                                  label={label}
                                                                                  size="small"
                                                                                  variant="outlined"
                                                                              />
                                                                          ))
                                                                        : '—'}
                                                                </Box>
                                                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                                    Trailers
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                                                                    {(sug.trailerIds?.length ?? 0) > 0
                                                                        ? (sug.trailerIds ?? []).map(tid => (
                                                                              <Chip
                                                                                  key={tid}
                                                                                  label={getTrailerName(tid)}
                                                                                  size="small"
                                                                                  variant="outlined"
                                                                              />
                                                                          ))
                                                                        : '—'}
                                                                </Box>
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    startIcon={<AddIcon />}
                                                                    fullWidth
                                                                    onClick={() => {
                                                                        setSelectedSuggestion(sug);
                                                                        setCreateDialogOpen(true);
                                                                    }}
                                                                >
                                                                    Create
                                                                </Button>
                                                            </CardContent>
                                                        </Card>
                                                    );
                                                })}
                                            </Box>
                                        ) : (
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }}>Sources</TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }}>Targets</TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }}>Trailers</TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }} align="right" />
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {suggestions.map((sug: SuggestedOperation, idx: number) => {
                                                            const sourceChips: Array<{ key: string; label: string }> = [];
                                                            if (sug.sourceStorageLocationIds.length > 0)
                                                                sourceChips.push({ key: 'src-storage', label: sourceName });
                                                            for (const eid of sug.sourceEventIds) {
                                                                const e = tripEvents.find(ev => ev.id === eid);
                                                                sourceChips.push({ key: eid, label: e?.name ?? eid });
                                                            }
                                                            const targetChips: Array<{ key: string; label: string }> = [];
                                                            if (sug.targetStorageLocationIds.length > 0)
                                                                targetChips.push({ key: 'tgt-storage', label: destName });
                                                            for (const eid of sug.targetEventIds) {
                                                                const e = tripEvents.find(ev => ev.id === eid);
                                                                targetChips.push({ key: eid, label: e?.name ?? eid });
                                                            }
                                                            return (
                                                                <TableRow key={idx} hover>
                                                                    <TableCell>{sug.label}</TableCell>
                                                                    <TableCell>
                                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                            {sourceChips.length > 0
                                                                                ? sourceChips.map(({ key, label }) => (
                                                                                      <Chip
                                                                                          key={key}
                                                                                          label={label}
                                                                                          size="small"
                                                                                          variant="outlined"
                                                                                      />
                                                                                  ))
                                                                                : '—'}
                                                                        </Box>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                            {targetChips.length > 0
                                                                                ? targetChips.map(({ key, label }) => (
                                                                                      <Chip
                                                                                          key={key}
                                                                                          label={label}
                                                                                          size="small"
                                                                                          variant="outlined"
                                                                                      />
                                                                                  ))
                                                                                : '—'}
                                                                        </Box>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                            {(sug.trailerIds?.length ?? 0) > 0
                                                                                ? (sug.trailerIds ?? []).map(tid => (
                                                                                      <Chip
                                                                                          key={tid}
                                                                                          label={getTrailerName(tid)}
                                                                                          size="small"
                                                                                          variant="outlined"
                                                                                      />
                                                                                  ))
                                                                                : '—'}
                                                                        </Box>
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        <Button
                                                                            size="small"
                                                                            variant="outlined"
                                                                            startIcon={<AddIcon />}
                                                                            onClick={() => {
                                                                                setSelectedSuggestion(sug);
                                                                                setCreateDialogOpen(true);
                                                                            }}
                                                                        >
                                                                            Create
                                                                        </Button>
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            )}

                            <OperationsListSection
                                activeOperations={activeOperations}
                                completedOperations={completedOperations}
                                operationProgress={op => getOperationProgress(op as Trip['packingOperations'][number])}
                                formatSummary={op => formatOperationSummary(op as OperationSummaryInput)}
                                onOperationClick={op => navigate(`/logistics/operation/${op.id}/tasks`)}
                                onCreateOperation={() => {
                                    setSelectedSuggestion(null);
                                    setCreateDialogOpen(true);
                                }}
                                expandedSections={expandedSections}
                                onAccordionChange={handleAccordionChange}
                                showTripLink
                                activeOperationElsewhereMessage={activeOperationElsewhereMessage}
                            />
                        </Box>
                    </TabPanel>

                    <TabPanel value={activeTab} index={TRIP_TAB_INVENTORY} idPrefix="trip-detail">
                        <Accordion
                            expanded={expandedSections.has('inventory')}
                            onChange={handleAccordionChange('inventory')}
                            sx={{ boxShadow: 2, '&:before': { display: 'none' } }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Inventory (global)
                                    </Typography>
                                    {insufficientGlobalItems.length > 0 && (
                                        <Chip
                                            icon={<WarningIcon />}
                                            label={`${insufficientGlobalItems.length} insufficient`}
                                            size="small"
                                            color="warning"
                                            variant="outlined"
                                        />
                                    )}
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {quantityPlans != null && quantityPlans.size > 0 ? (
                                    <>
                                        {insufficientGlobalItems.length > 0 && (
                                            <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
                                                {insufficientGlobalItems.length}{' '}
                                                {insufficientGlobalItems.length === 1 ? 'item has' : 'items have'} insufficient global
                                                inventory for this trip
                                            </Alert>
                                        )}
                                        <QuantitySummary
                                            quantityPlans={quantityPlans}
                                            getAvailable={getAvailableGlobal}
                                            getItemTypeName={getItemTypeName}
                                            getItemTypeCategory={getItemTypeCategory}
                                            getEventName={id => eventsMap.get(id)?.name ?? id}
                                            getTrailerName={getTrailerName}
                                        />
                                    </>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No quantity plan for this trip. Add events, set source and destination, then recalculate plan.
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    </TabPanel>

                    <TabPanel value={activeTab} index={TRIP_TAB_ISSUES} idPrefix="trip-detail">
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
                            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                                <Accordion
                                    expanded={expandedSections.has('issuesTrailerCargo')}
                                    onChange={handleAccordionChange('issuesTrailerCargo')}
                                    disableGutters
                                    elevation={0}
                                    sx={{ '&:before': { display: 'none' } }}
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            Trailer–cargo routing
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {trailerCargoIssues.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">
                                                No trailer–cargo conflicts.
                                            </Typography>
                                        ) : (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                {trailerCargoIssues.map((issue, i) => (
                                                    <TrailerCargoRoutingIssueAlert
                                                        key={`tc-${issue.cargoItemTypeId}-${i}-${issue.message.slice(0, 24)}`}
                                                        issue={issue}
                                                        getItemTypeName={getItemTypeName}
                                                        getTrailerName={getTrailerName}
                                                        highlight={
                                                            recalcHighlightCargoItemTypeId !== null &&
                                                            issue.cargoItemTypeId === recalcHighlightCargoItemTypeId
                                                        }
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            </Paper>

                            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                                <Accordion
                                    expanded={expandedSections.has('issuesInventory')}
                                    onChange={handleAccordionChange('issuesInventory')}
                                    disableGutters
                                    elevation={0}
                                    sx={{ '&:before': { display: 'none' } }}
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            Global inventory vs plan
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {insufficientGlobalItems.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">
                                                No inventory shortages for the current quantity plan.
                                            </Typography>
                                        ) : (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                {insufficientGlobalItems.map(item => (
                                                    <Alert key={item.itemTypeId} severity="warning">
                                                        <Link to={getInventoryItemUrl(item.itemTypeId)}>
                                                            {getItemTypeName(item.itemTypeId)}
                                                        </Link>
                                                        : need {item.required}, have {getAvailableGlobal(item.itemTypeId)} (short by{' '}
                                                        {item.shortage}).
                                                    </Alert>
                                                ))}
                                            </Box>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            </Paper>

                            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                                <Accordion
                                    expanded={expandedSections.has('issuesRoute')}
                                    onChange={handleAccordionChange('issuesRoute')}
                                    disableGutters
                                    elevation={0}
                                    sx={{ '&:before': { display: 'none' } }}
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            Route
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {routeIssueEntries.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">
                                                No route conflicts or warnings.
                                            </Typography>
                                        ) : (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                {routeIssueEntries.map(entry => (
                                                    <Alert key={entry.key} severity={entry.severity}>
                                                        {entry.text}
                                                    </Alert>
                                                ))}
                                            </Box>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            </Paper>

                            {issuesTabCount === 0 && (
                                <Alert severity="success" sx={{ mt: 1 }}>
                                    No issues detected for the checks above.
                                </Alert>
                            )}
                        </Box>
                    </TabPanel>

                    <CreateOperationDialog
                        open={createDialogOpen}
                        onClose={() => {
                            setCreateDialogOpen(false);
                            setSelectedSuggestion(null);
                        }}
                        initialSuggestion={selectedSuggestion ?? undefined}
                        tripId={tripId ?? undefined}
                        tripName={displayTrip?.name}
                        tripLpData={tripLpData}
                        allowedEventIds={tripEvents.map(e => e.id)}
                        allowedStorageLocationIds={tripStorageLocationIds}
                        tripSourceStorageLocationId={sourceStorageLocationId ?? undefined}
                        tripDestStorageLocationId={destStorageLocationId ?? undefined}
                    />

                    <EditTripDialog
                        open={editDialogOpen}
                        onClose={() => setEditDialogOpen(false)}
                        trip={trip}
                        onSaved={() => setEditDialogOpen(false)}
                    />

                    <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                        <DialogTitle>Delete trip</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                Are you sure you want to delete &quot;{displayTrip?.name ?? 'this trip'}&quot;? This cannot be undone.
                            </DialogContentText>
                            {deleteError && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    {deleteError}
                                </Alert>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                            <Button
                                color="error"
                                variant="contained"
                                startIcon={<DeleteIcon />}
                                onClick={async () => {
                                    if (!tripId) return;
                                    setDeleteError(null);
                                    try {
                                        await db.transact([db.tx.trips[tripId].delete()]);
                                        setDeleteDialogOpen(false);
                                        navigate('/logistics/trips');
                                    } catch (e) {
                                        setDeleteError(e instanceof Error ? e.message : 'Failed to delete trip');
                                    }
                                }}
                            >
                                Delete
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Box>
            </Box>
        </DefaultLayout>
    );
}
